import React, { useEffect, useRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { DateFilterType } from "./types";

interface DateTypeDropdownProps {
  value: DateFilterType;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (value: DateFilterType) => void;
}

export const DateTypeDropdown: React.FC<DateTypeDropdownProps> = ({ value, open, setOpen, onChange }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, setOpen]);

  const dateTypeValueMap = {
    "createdAt": "Created date",
    "voucherCreateDate": "Activation date",
    "expiryDate": "Expiry date"
  }

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">Date type</label>
      <button
        type="button"
        className="w-full min-h-[52px] flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md text-sm bg-white shadow-sm hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        onClick={() => setOpen((curr) => !curr)}
      >
        <div className="text-left">
          <p className="text-sm font-medium text-gray-800">
            {dateTypeValueMap[value]}
          </p>
          <p className="text-xs text-gray-500">Date field for filtering</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg p-1">
          <button
            type="button"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
              value === "createdAt" ? "font-semibold bg-gray-100" : ""
            }`}
            onClick={() => {
              onChange("createdAt");
              setOpen(false);
            }}
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                value === "createdAt" ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
              }`}
            >
              {value === "createdAt" && <Check className="w-3 h-3 text-white" />}
            </span>
            Created date
          </button>
          <button
            type="button"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
              value === "voucherCreateDate" ? "font-semibold bg-gray-100" : ""
            }`}
            onClick={() => {
              onChange("voucherCreateDate");
              setOpen(false);
            }}
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                value === "voucherCreateDate" ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
              }`}
            >
              {value === "voucherCreateDate" && <Check className="w-3 h-3 text-white" />}
            </span>
            Activation date
          </button>
          <button
            type="button"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
              value === "expiryDate" ? "font-semibold bg-gray-100" : ""
            }`}
            onClick={() => {
              onChange("expiryDate");
              setOpen(false);
            }}
          >
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                value === "expiryDate" ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
              }`}
            >
              {value === "expiryDate" && <Check className="w-3 h-3 text-white" />}
            </span>
            Expiry date
          </button>
        </div>
      )}
    </div>
  );
};
